import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface RemovedChannelsTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}

type ChannelInfo = {
  name: string;
  price: number | null;
  description: string | null;
};

export function RemovedChannelsTable({ changes, isLoading }: RemovedChannelsTableProps) {
  const [channelData, setChannelData] = useState<Record<number, ChannelInfo>>({});

  // Filter for deleted channels only (entity_type = 'channels' and action_type = 'DELETE' with field_name = 'entity')
  const deletedChannelChanges = changes?.filter(change =>
    change.entity_type === 'channels' &&
    change.action_type === 'DELETE' &&
    change.field_name === 'entity'
  ) || [];


  // Parse channel data from the old_value JSON
  useEffect(() => {
    const parseChannelData = () => {
      const parsedData: Record<number, ChannelInfo> = {};

      deletedChannelChanges.forEach(change => {
        if (change.old_value) {
          try {
            const channelInfo = JSON.parse(change.old_value);
            parsedData[change.entity_id] = {
              name: channelInfo.name || `Channel #${change.entity_id}`,
              price: channelInfo.price || null,
              description: channelInfo.description || null
            };
          } catch (error) {
            console.error('Failed to parse channel data:', error);
            parsedData[change.entity_id] = {
              name: `Channel #${change.entity_id}`,
              price: null,
              description: null
            };
          }
        }
      });

      setChannelData(parsedData);
    };

    if (deletedChannelChanges.length > 0) {
      parseChannelData();
    }
  }, [deletedChannelChanges]);

  if (isLoading) return <div>Loading...</div>;

  if (deletedChannelChanges.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No removed channels found.
      </div>
    );
  }

  // Sort by deletion timestamp (newest first)
  const sortedChanges = deletedChannelChanges.sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Channel Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Price</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Deleted At</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedChanges.map((change) => {
          const channel = channelData[change.entity_id];

          return (
            <TableRow key={change.id} className="hover:bg-muted/50">
              {/* Channel Name */}
              <TableCell className="font-medium">
                <div className="font-semibold text-red-600 line-through">
                  {channel?.name || `Channel #${change.entity_id}`}
                </div>
              </TableCell>

              {/* Action */}
              <TableCell>
                <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100">
                  DELETED
                </Badge>
              </TableCell>

              {/* Price */}
              <TableCell>
                <div className="text-xs text-red-600 line-through">
                  {channel?.price !== null && channel?.price !== undefined ? `$${channel.price}` : 'N/A'}
                </div>
              </TableCell>

              {/* Description */}
              <TableCell>
                <div className="text-xs text-red-600 line-through max-w-xs truncate">
                  {channel?.description || 'N/A'}
                </div>
              </TableCell>

              {/* Deleted At */}
              <TableCell>
                <div className="text-xs">{formatDateTime(change.created_at)}</div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}